import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/form';
import { AuthShell } from '@/features/auth/auth-shell';
import { NewPasswordFields, passwordIssue } from '@/features/auth/new-password-fields';
import { useHashToken } from '@/features/auth/use-hash-token';
import { api, errorMessage } from '@/lib/api';

export const Route = createFileRoute('/reset-password')({ component: ResetPage });

function ResetPage() {
  const token = useHashToken();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const reset = useMutation({ mutationFn: () => api.post('/v1/auth/password/reset', { token, password }) });
  const issue = passwordIssue(password, confirm);

  const signIn = (
    <Link to="/login" search={{}} className="text-link underline-offset-4 hover:underline">
      Go to sign in
    </Link>
  );

  if (!token) {
    return (
      <AuthShell title="Link not valid" description="This reset link is incomplete. Open the link from your email again, or request a new one." footer={signIn}>
        <Link to="/forgot-password" className="text-sm text-link underline-offset-4 hover:underline">
          Request a new link
        </Link>
      </AuthShell>
    );
  }
  if (reset.isSuccess) {
    return (
      <AuthShell title="Password changed" description="You’ve been signed out everywhere. Sign in with your new password and your authenticator app." footer={signIn}>
        <span />
      </AuthShell>
    );
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!issue) reset.mutate();
  }

  return (
    <AuthShell title="Choose a new password" footer={signIn}>
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <FormError message={reset.isError ? errorMessage(reset.error) : null} />
        <NewPasswordFields password={password} confirm={confirm} onPassword={setPassword} onConfirm={setConfirm} />
        <Button type="submit" size="lg" disabled={!!issue || reset.isPending}>
          {reset.isPending ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </AuthShell>
  );
}
